import {
  Container,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useState } from "react";
import { useFragment, useLazyLoadQuery } from "react-relay";
import { UploadableMap } from "relay-runtime";
import { AppClipDefaultCardQuery } from "./__generated__/AppClipDefaultCardQuery.graphql";
import {
  AppClipActionTitle,
  AppClipDefaultCard_appConfig$key,
} from "./__generated__/AppClipDefaultCard_appConfig.graphql";
import CardSection from "../../../../components/CardSection";
import InfoText from "../../../../components/InfoText";
import SaveButton, { SavedState } from "../../../../components/SaveButton";
import UploadedFileCard from "../../../../components/UploadedFileCard";
import useBrandAppConfigUpdateMutation from "../../../../mutations/useBrandAppConfigUpdateMutation";

enum AppClipField {
  HEADER_IMAGE = "appClipHeaderImage",
  SUBTITLE = "appClipSubtitle",
  ACTION_TEXT = "appClipActionText",
}

const query = graphql`
  query AppClipDefaultCardQuery($id: ID!) {
    brand(id: $id) {
      appConfig {
        ...AppClipDefaultCard_appConfig
      }
    }
  }
`;

const appConfigFragment = graphql`
  fragment AppClipDefaultCard_appConfig on BrandAppConfigType {
    id
    appClipHeaderImageUrl
    appClipSubtitle
    appClipActionText
  }
`;

const AppClipDefaultCard = ({ brandID }: { brandID: string }) => {
  const data = useLazyLoadQuery<AppClipDefaultCardQuery>(query, {
    id: brandID,
  });
  const appConfig = useFragment<AppClipDefaultCard_appConfig$key>(
    appConfigFragment,
    data.brand.appConfig
  );

  const [headerImageUrl, setHeaderImageUrl] = useState<string | null>(
    appConfig.appClipHeaderImageUrl
  );
  const [subtitle, setSubtitle] = useState<string>(appConfig.appClipSubtitle);
  const [actionText, setActionText] = useState<AppClipActionTitle>(
    appConfig.appClipActionText
  );

  const [saveButtonState, setSaveButtonState] = useState<SavedState>(
    headerImageUrl ? SavedState.SAVED : SavedState.DISABLED
  );

  const [mutationErrors, setMutationErrors] = useState<
    Array<{
      field: string;
      message: string;
    }>
  >([]);
  const [mediaUploadable, setMediaUploadable] = useState<UploadableMap>({});

  const [saveMutation, isMutationInFlight] = useBrandAppConfigUpdateMutation(
    {
      id: appConfig.id,
      appClipHeaderImage: headerImageUrl === null ? null : undefined,
      appClipSubtitle: subtitle,
      appClipActionText: actionText,
    },
    () => {
      setSaveButtonState(SavedState.SAVED);
    },
    mediaUploadable,
    setMutationErrors
  );

  const resetMutationError = (field: AppClipField) => {
    setMutationErrors((prev) => {
      return prev.filter((error) => error.field !== field);
    });
  };

  return (
    <Container maxWidth="md">
      <InfoText variant="info">
        These changes will only go live with the next app update.
      </InfoText>

      <CardSection
        actions={
          <SaveButton
            savedState={
              isMutationInFlight ? SavedState.DISABLED : saveButtonState
            }
            onClick={saveMutation}
          />
        }
        title={"Default App Clip Card"}
        content={
          <Stack spacing={2} width="100%">
            <UploadedFileCard
              error={
                mutationErrors.find(
                  (error) => error.field === AppClipField.HEADER_IMAGE
                )?.message ?? undefined
              }
              fileURL={headerImageUrl}
              fileType={".png, .jpg"}
              htmlLabel={"header-image-upload"}
              inputLabel={"Header Image"}
              inputText={"Upload a .png or .jpg, 1800x1200px"}
              mediaType="PHOTO"
              validPhotoDimensions={[{ width: 1800, height: 1200 }]}
              previewWidth="100%"
              onClose={() => {
                setHeaderImageUrl(null);
                delete mediaUploadable[AppClipField.HEADER_IMAGE];

                resetMutationError(AppClipField.HEADER_IMAGE);

                saveButtonState !== SavedState.ENABLED &&
                  setSaveButtonState(SavedState.ENABLED);
              }}
              onUpload={(event: React.ChangeEvent<HTMLInputElement>) => {
                const file = event?.target?.files?.[0];
                if (file == null) {
                  return;
                }

                const url = URL.createObjectURL(file);
                setHeaderImageUrl(url);
                const newUploadables: UploadableMap = {};
                newUploadables[AppClipField.HEADER_IMAGE] = file;
                setMediaUploadable(newUploadables);

                resetMutationError(AppClipField.HEADER_IMAGE);

                saveButtonState !== SavedState.ENABLED &&
                  setSaveButtonState(SavedState.ENABLED);
              }}
            />

            <TextField
              fullWidth
              value={subtitle}
              label="Subtitle"
              variant="outlined"
              required
              onChange={(event) => {
                setSubtitle(event.target.value);
                saveButtonState !== SavedState.ENABLED &&
                  setSaveButtonState(SavedState.ENABLED);
              }}
              inputProps={{
                maxLength: 56,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {subtitle.length} / 56
                  </InputAdornment>
                ),
              }}
            />

            <FormControl fullWidth>
              <InputLabel id="action-button-text-label">
                Action Button Text
              </InputLabel>
              <Select
                fullWidth
                value={actionText}
                label="Action Button Text"
                labelId="action-button-text-label"
                variant="outlined"
                onChange={(event) => {
                  setActionText(event.target.value as AppClipActionTitle);
                  saveButtonState !== SavedState.ENABLED &&
                    setSaveButtonState(SavedState.ENABLED);
                }}
              >
                <MenuItem value={"OPEN" as AppClipActionTitle}>Open</MenuItem>
                <MenuItem value={"VIEW" as AppClipActionTitle}>View</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        }
      />
    </Container>
  );
};

export default AppClipDefaultCard;
